/**
 * L1 §4 — repositories. Free space per backup target, as `Meter`s (design
 * rules §2: a ratio against a limit is a meter, not a chart).
 *
 * The honesty rule that shapes this panel: capacity may be null when the
 * source's cached container values are unavailable (`PAYLOAD.md` field notes),
 * and a null must **render as unknown, never as 0** — a repository reporting
 * "0 % free" that is actually just unreadable would send someone to an incident
 * that does not exist, and one reporting "100 % free" would hide one that does.
 * Unknown rows therefore carry no meter at all: an empty track is a claim, and
 * there is nothing to claim. They are counted in the caption instead, so the
 * estate total is never quietly computed from a subset.
 *
 * Thresholds live in `status.ts`, so the meter tone and the caption cannot
 * drift apart.
 */

import { EmptyState, Meter, Typography } from "@norican/react";

import type { BackupRepositoryRow, BackupRepositorySummary } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";

import { PanelCard } from "./panel-card";
import {
  formatBytes,
  freePercent,
  REPO_DANGER_FREE_PCT,
  repositoryTone,
  sourceLabel,
} from "./status";

interface RepositoriesSectionProps {
  repositories: BackupRepositorySummary;
  /** Selected sources from the board FilterRow ([] = all). */
  sourceIds: string[];
}

export function RepositoriesSection({ repositories, sourceIds }: RepositoriesSectionProps) {
  const rows = repositories.repositories.filter(
    (repository) => sourceIds.length === 0 || sourceIds.includes(repository.source),
  );
  const unknown = rows.filter((repository) => freePercent(repository.used_pct) == null).length;
  const tight = rows.filter((repository) => {
    const free = freePercent(repository.used_pct);
    return free != null && free < REPO_DANGER_FREE_PCT;
  }).length;

  return (
    <section id="backup-repositories" className="scroll-mt-6">
      <PanelCard
        title="Repositories"
        caption={
          tight > 0
            ? `${formatCount(tight)} below ${REPO_DANGER_FREE_PCT}% free`
            : unknown > 0
              ? `${formatCount(unknown)} of ${formatCount(rows.length)} report no capacity figures`
              : `${formatCount(rows.length)} targets · ${formatBytes(
                  repositories.free_bytes,
                )} free of ${formatBytes(repositories.total_bytes)}`
        }
      >
        {rows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No repositories reported"
            description="The collector sends repository capacity alongside the job summary; none arrived in the current scope."
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {rows.map((repository) => (
              <RepositoryMeter
                key={`${repository.source}:${repository.name}`}
                repository={repository}
              />
            ))}
          </div>
        )}
        {unknown > 0 && rows.length > 0 && (
          <Typography variant="caption" tone="muted">
            The estate totals in the caption above exclude the {formatCount(unknown)}{" "}
            {unknown === 1 ? "target" : "targets"} whose capacity is unknown — they are missing from
            the sum, not counted as empty.
          </Typography>
        )}
      </PanelCard>
    </section>
  );
}

function RepositoryMeter({ repository }: { repository: BackupRepositoryRow }) {
  const free = freePercent(repository.used_pct);
  const tone = repositoryTone(free);

  const header = (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <span className="text-fg text-sm font-medium">{repository.name}</span>
      <span className="text-fg-muted text-xs">
        {sourceLabel(repository.source)}
        {repository.repo_type ? ` · ${repository.repo_type}` : ""}
      </span>
    </div>
  );

  if (free == null) {
    // No meter: an empty track would read as "empty", and the truth is "unread".
    return (
      <div className="flex flex-col gap-1">
        {header}
        <Typography variant="caption" tone="muted">
          Capacity unknown — the source reported no usable free/total figures
          {repository.free_bytes != null ? ` (free: ${formatBytes(repository.free_bytes)})` : ""}
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {header}
      <Meter.Root
        value={free}
        min={0}
        max={100}
        size="sm"
        tone={tone === "neutral" ? "accent" : tone}
        aria-valuetext={`${free.toFixed(1)} per cent free — ${formatBytes(
          repository.free_bytes,
        )} of ${formatBytes(repository.total_bytes)} on ${repository.name}`}
      >
        <div className="mb-1 flex items-baseline justify-between gap-3">
          <Meter.Label>
            <Typography variant="caption" tone="muted">
              Free space
            </Typography>
          </Meter.Label>
          <Meter.Value>
            {() => (
              <Typography variant="caption" tone="muted" tabular>
                {free.toFixed(1)}% · {formatBytes(repository.free_bytes)} of{" "}
                {formatBytes(repository.total_bytes)}
              </Typography>
            )}
          </Meter.Value>
        </div>
        <Meter.Track>
          <Meter.Indicator />
        </Meter.Track>
      </Meter.Root>
    </div>
  );
}
